import { toIsoDate } from "./apiDate";
import { buildSeriesDetailsUrl, buildShowUrl } from "./showSlug";
import type { TranslationKey } from "./i18n/locales";

// Which page the tags describe (SHO-757): the series details page or the
// player. They used to be one page, so one target carried both roles.
export type ShowMetaTarget = "player" | "series";

// The `<title>` suffix of each page, one key per target. The two pages are
// both self-canonical, so a byte-identical title (and they share description
// and image already) would read as a duplicate and let Google pick the
// canonical itself — against the series page, the one meant to rank. Every
// layer that applies the tags resolves its suffix through this table.
export const SEO_TITLE_SUFFIX_KEY = {
  player: "player_seo_title_suffix_web",
  series: "series_details_seo_title_suffix_web",
} as const satisfies Record<ShowMetaTarget, TranslationKey>;

export type ShowMetaTagsInput = {
  displayTitle: string;
  displayDescription: string;
  displayHorizontalThumbnail?: string | null;
  displayImage: string;
  // Show identity, not the URL segment the visitor arrived on: the canonical
  // must be the slugged form even for someone who landed on `/94/ep-12`,
  // otherwise one show declares two competing canonicals.
  showId: string;
  title: string;
  publishedAt?: string | null;
  createdAt: string;
  unlisted: boolean;
  origin: string;
  titleSuffix: string;
  target: ShowMetaTarget;
};

export type ShowJsonLd = {
  "@context": "https://schema.org";
  "@type": "TVSeries";
  name: string;
  description?: string;
  image: string;
  url: string;
};

export type VideoJsonLd = {
  "@context": "https://schema.org";
  "@type": "VideoObject";
  name: string;
  description?: string;
  thumbnailUrl: string;
  uploadDate: string;
};

export type ShowMetaTagValues = {
  title: string;
  description: string | null;
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string | null;
  ogImage: string;
  ogVideoImage: string;
  ogType: "video.tv_show";
  ogUrl: string;
  twitterCard: "summary_large_image";
  robots: "noindex" | null;
  // One value, serialized into ONE script by both layers: a second script
  // would escape the single `data-show-meta` handle the hook upserts through.
  jsonLd: [ShowJsonLd] | [VideoJsonLd];
};

/**
 * Derives the client-side SEO/social tags for a show page (SHO-664).
 * Canonicalized at the series level, using the slugged series segment built
 * from the show's own identity: every episode of the same show shares an
 * identical title/description, so a distinct canonical per episode would
 * read as duplicate content to Google.
 *
 * Two pages describe a show, one role each (SHO-757). The series details page
 * (`target: "series"`, canonical `/series/{param}`) is the one Google ranks for
 * the show and carries the `TVSeries` block. The player (`target: "player"`,
 * canonical `/{param}/0`) is a video page and carries the `VideoObject`: the
 * two blocks on two pages made them compete for the same show.
 *
 * The player still declares its image twice over on purpose (SHO-725):
 * `og:image` feeds the social unfurl, but Google's *video* indexing only reads
 * a thumbnail from `og:video:image` and `VideoObject.thumbnailUrl` — without
 * them Search Console reports "No thumbnail URL provided". All carry the same
 * URL, which Google requires of a video's thumbnail sources.
 */
export const deriveShowMetaTags = ({
  displayTitle,
  displayDescription,
  displayHorizontalThumbnail,
  displayImage,
  showId,
  title,
  publishedAt,
  createdAt,
  unlisted,
  origin,
  titleSuffix,
  target,
}: ShowMetaTagsInput): ShowMetaTagValues => {
  const canonicalUrl =
    target === "series"
      ? buildSeriesDetailsUrl(origin, title, showId)
      : buildShowUrl(origin, title, showId);
  const description = displayDescription ? displayDescription : null;
  const image = displayHorizontalThumbnail || displayImage;
  const jsonLdBase = {
    "@context": "https://schema.org" as const,
    name: displayTitle,
    ...(description ? { description } : {}),
  };

  return {
    title: `${displayTitle} — ${titleSuffix}`,
    description,
    canonicalUrl,
    ogTitle: displayTitle,
    ogDescription: description,
    ogImage: image,
    ogVideoImage: image,
    ogType: "video.tv_show",
    ogUrl: canonicalUrl,
    twitterCard: "summary_large_image",
    // An unlisted show is reachable by direct link only (SHO-748): its page
    // keeps every tag a shared link unfurls from, and only asks not to be
    // indexed.
    robots: unlisted ? "noindex" : null,
    jsonLd:
      target === "series"
        ? [{ ...jsonLdBase, "@type": "TVSeries", image, url: canonicalUrl }]
        : [
            {
              ...jsonLdBase,
              "@type": "VideoObject",
              thumbnailUrl: image,
              uploadDate: toIsoDate(publishedAt || createdAt),
            },
          ],
  };
};

/** The show fields the tags derive from — selected by GetShow and GetSeriesDetails alike. */
export type ShowMetaSource = {
  id: string;
  title: string;
  displayTitle: string;
  displayDescription: string;
  displayHorizontalThumbnail?: { original: string } | null;
  displayImage: { original: string };
  publishedAt?: string | null;
  createdAt: string;
  unlisted: boolean;
};

/** One mapping from a show to the tags' input, for every layer that has one. */
export const showMetaInput = (
  show: ShowMetaSource,
  target: ShowMetaTarget,
  origin: string,
  titleSuffix: string,
): ShowMetaTagsInput => ({
  displayTitle: show.displayTitle,
  displayDescription: show.displayDescription,
  displayHorizontalThumbnail: show.displayHorizontalThumbnail?.original,
  displayImage: show.displayImage.original,
  showId: show.id,
  title: show.title,
  publishedAt: show.publishedAt,
  createdAt: show.createdAt,
  unlisted: show.unlisted,
  origin,
  titleSuffix,
  target,
});
